/* eslint-disable import/no-extraneous-dependencies */
import React, { Suspense } from 'react';
import { Router, Route, Switch, Redirect } from 'react-router-dom';
import isEqual from 'lodash/isEqual';
import PropTypes from 'prop-types';
import { bindActionCreators } from 'redux';
import { connect } from 'react-redux';
import { Container } from 'semantic-ui-react';
import 'semantic-ui-css/semantic.min.css';
import 'font-awesome/css/font-awesome.min.css';
import TaskViewPage from './components/taskView/TaskViewPage/TaskViewPage';
import HeaderPage from './components/common/HeaderPage';
import NotAuthorized from './components/common/NotAuthorized';
import 'bryntum-scheduler/scheduler.stockholm.css';
import 'ag-grid-community/styles/ag-grid.css';
import 'ag-grid-community/styles/ag-theme-balham.css';
import 'ag-grid-community/styles/ag-theme-alpine.css';
import './App.css';
import './forms.css';
import './ag-grid.css';
import * as actions from './actions/appActions';
import * as taskTypeActionsFunctions from './actions/taskTypeActions';
import * as teamActionsFunctions from './actions/teamActions';
import * as areaActionsFunctions from './actions/areaActions';
import * as allTaskViewActions from './components/taskView/TaskViewPage/redux/actions';
import ReportError from './components/ReportError/ReportError';
import logUtils from './HelperMethods/LogUtils';
import { RESOURCES } from './HelperMethods/role-resource-constants';
import AuthRoute from './components/common/AuthRoute';
import Joyn2Loader from './components/common/Joyn2Loader';
import TopHeader from './components/common/TopHeader';
import { actionRecordUtils } from './HelperMethods/LocalStorageUtils';

const LogsComponent = React.lazy(() => import('./components/logs/logsComponent'));
const TaskSetupPage = React.lazy(() => import('./components/taskSetup/TaskSetupPage'));
const UserManagement = React.lazy(() => import('./components/userManagement/UserManagement'));
const AssetManagement = React.lazy(() => import('./components/assetManagement/AssetManagement'));
const FormManagement = React.lazy(() => import('./components/formManagement/FormManagement'));
const EngineScheduleManagement = React.lazy(
  () => import('./components/engineScheduleManagement/EngineScheduleManagement')
);
const GeneralConfigSetup = React.lazy(() => import('./components/generalConfig/GeneralConfigSetup'));
const PrioritizationConfigSetup = React.lazy(
  () => import('./components/prioritizationConfig/PrioritizationConfigSetup')
);
const RequestManagement = React.lazy(() => import('./components/approvalGridView/RequestManagement'));
const ReportConfigSetup = React.lazy(() => import('./components/reports/ReportConfigSetup'));
// show app on login with tenantId setup
class App extends React.PureComponent {
  constructor(props) {
    super(props);
    this.state = {
      hasError: false,
      iframepresentState: false,
    };

    if (window.location.pathname.includes('index.html')) {
      if (localStorage.getItem('pathname')) {
        props.history.replace(localStorage.getItem('pathname'));
      } else {
        props.history.replace('/taskViewPage');
      }
    }
  }

  async componentDidMount() {
    const { appActions } = this.props;
    await appActions.bulkDownloadInitialData(this.props);
    if (!isEqual(window.location, window.parent.location)) {
      this.setState({
        iframepresentState: true,
      });
    }
  }

  componentDidCatch(error, info) {
    // Display fallback UI
    // You can also log the error to an error reporting service
    logUtils.logError(error);
    this.setState({ hasError: true, error, errorInfo: info });
    console.error(error);
    console.log(info);
  }

  render() {
    const { state, selectedField, isAppLoading, errorMessage, history } = this.props;
    const { hasError, error, errorInfo } = this.state;

    if (isAppLoading) {
      return <div className="joyn2-loader" />;
    }
    if (hasError) {
      return <ReportError errorLog={error} errorInfo={errorInfo} appState={state} />;
    }
    if (errorMessage) {
      return <div style={{ textAlign: 'center', fontWeight: 'bold', fontSize: '21px' }}>{errorMessage}</div>;
    }
    return (
      <Router history={history}>
        <Container fluid style={{ height: '100%' }}>
          {!this.state.iframepresentState && <TopHeader />}
          {!actionRecordUtils.GET_ACTION_RECORD() && <HeaderPage selectedField={selectedField} />}
          <Suspense fallback={<Joyn2Loader />}>
            <Switch>
              <Redirect exact from="/" to="/taskViewPage" />
              <AuthRoute
                path="/taskViewPage"
                redirectUrl="/taskSetup"
                view={RESOURCES.VIEW.TASK_VIEW}
                children={(routeProps) => <TaskViewPage {...routeProps} />}
              />
              <AuthRoute
                path="/taskSetup"
                view={[RESOURCES.VIEW.TEAM_VIEW, RESOURCES.VIEW.AREA_VIEW, RESOURCES.VIEW.TASK_TYPE_VIEW]}
                redirectUrl="/usermanagement"
                children={(routeProps) => <TaskSetupPage {...routeProps} />}
              />
              <AuthRoute
                path="/requestViewPage"
                view={RESOURCES.VIEW.REQUEST_VIEW}
                redirectUrl="/usermanagement"
                children={(routeProps) => <RequestManagement {...routeProps} />}
              />
              <AuthRoute
                path="/usermanagement"
                view={RESOURCES.VIEW.USER_MANAGEMENT_VIEW}
                redirectUrl="/logs"
                children={(routeProp) => <UserManagement {...routeProp} />}
              />
              <AuthRoute
                path="/logs"
                view={RESOURCES.VIEW.LOGS_VIEW}
                redirectUrl="/assetmanagement"
                children={(routeProps) => <LogsComponent {...routeProps} />}
              />
              <AuthRoute
                path="/assetmanagement"
                view={RESOURCES.VIEW.ASSET_MANAGEMENT_VIEW}
                redirectUrl="/formmanagement"
                children={(routeProps) => <AssetManagement {...routeProps} />}
              />
              <AuthRoute
                path="/formmanagement"
                view={RESOURCES.VIEW.FORM_MANAGEMENT_VIEW}
                redirectUrl="/generalconfig"
                children={(routeProps) => <FormManagement {...routeProps} />}
              />
              <AuthRoute
                path="/engineschedulemanagement"
                view={RESOURCES.VIEW.ENGINE_SCHEDULE_MANAGEMENT_VIEW}
                redirectUrl="/generalconfig"
                children={(routeProps) => <EngineScheduleManagement {...routeProps} />}
              />
              <AuthRoute
                path="/reports"
                view={RESOURCES.VIEW.EXPORT_MANGEMENT_VIEW}
                redirectUrl="/taskViewPage"
                children={(routeProps) => <ReportConfigSetup {...routeProps} />}
              />
              <AuthRoute
                path="/generalconfig"
                viewsHolder
                view={[
                  RESOURCES.VIEW.GLOBAL_ATTRIBUTE_VIEW,
                  RESOURCES.VIEW.APPROVAL_MATRIX_MANAGEMENT_VIEW,
                  RESOURCES.VIEW.PICKLIST_MANAGEMENT_VIEW,
                  RESOURCES.VIEW.ROLE_MANAGEMENT_VIEW,
                  RESOURCES.VIEW.CONTACT_MANAGEMENT_VIEW,
                  RESOURCES.VIEW.INSTRUCTION_MANAGEMENT_VIEW,
                  RESOURCES.VIEW.VENDOR_MANAGEMENT_VIEW,
                  RESOURCES.VIEW.ATTACHMENT_MANAGEMENT_VIEW,
                  RESOURCES.VIEW.WATCHER_GROUP_MANAGEMENT_VIEW,
                ]}
                redirectUrl="/404-path"
                children={(routeProps) => <GeneralConfigSetup {...routeProps} />}
              />
              <AuthRoute
                path="/prioritizationconfig"
                view={RESOURCES.VIEW.PRIORITIZATION_MANAGEMENT_VIEW}
                redirectUrl="/404-path"
                children={(routeProps) => <PrioritizationConfigSetup {...routeProps} />}
              />
              <Route path="*" component={NotAuthorized} />
            </Switch>
          </Suspense>
          <div style={{ position: 'fixed', right: '16px', bottom: '0px' }}>
            Version: {process.env.REACT_APP_VERSION}
          </div>
          <div style={{ position: 'fixed', left: '50%', top: '50%', display: 'none' }}>
            {process.env.REACT_APP_COMMIT_HASH_CODE || 'Unknown Hash Code'}
          </div>
        </Container>
      </Router>
    );
  }
}

function mapDispatchToProps(dispatch) {
  return {
    teamActions: bindActionCreators(teamActionsFunctions, dispatch),
    taskTypeActions: bindActionCreators(taskTypeActionsFunctions, dispatch),
    appActions: bindActionCreators(actions, dispatch),
    areaActions: bindActionCreators(areaActionsFunctions, dispatch),
    taskViewActions: bindActionCreators(allTaskViewActions, dispatch),
  };
}
function mapStateToProps(state) {
  return {
    state,
    selectedField: state.appReducer.selectedField,
    isAppLoading: state.appReducer.isAppLoading,
    errorMessage: state.appReducer.errorMessage,
  };
}
App.propTypes = {
  history: PropTypes.object.isRequired,
  state: PropTypes.object.isRequired,
  isAppLoading: PropTypes.bool.isRequired,
  errorMessage: PropTypes.string,
  selectedField: PropTypes.shape({
    id: PropTypes.string,
  }),
  appActions: PropTypes.shape({
    bulkDownloadInitialData: PropTypes.func.isRequired,
    onFieldChange: PropTypes.func.isRequired,
    onAreaSelected: PropTypes.func.isRequired,
  }).isRequired,
  teamActions: PropTypes.shape({
    loadAllTeams: PropTypes.func.isRequired,
  }).isRequired,
  taskTypeActions: PropTypes.shape({
    loadAllTaskTypes: PropTypes.func.isRequired,
  }).isRequired,
  areaActions: PropTypes.shape({
    loadAreasForFieldId: PropTypes.func.isRequired,
  }).isRequired,
  taskViewActions: PropTypes.shape({
    loadUsersForField: PropTypes.func.isRequired,
  }).isRequired,
};

export default connect(mapStateToProps, mapDispatchToProps)(App);
